// Slide-in detail panel for one token: identity and contract links, the
// latest figures, a history chart, and every other venue issuing the same
// stock. Reads /v1/tokens/{id}, its /history and /v1/assets/{ticker} (App
// keeps ?token=<id> in the address bar so a view can be shared). Non-modal at
// desktop width so the grid behind stays usable; full-width with the page
// scroll locked on phones. Escape closes; focus returns to whatever opened it.
// A missing figure renders "—" or a short reason, never 0.

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { chainLabel as chainName, explorerUrl } from "../api";
import { classById, fetchClassBoard, isBoardClass, priceBasisLabel, priceBasisMeaning, type AssetClassId, type BoardClassId, type ClassEntry } from "../classes";
import { profileRows, sourceLabel } from "../profile";
import { ago, amount, compactUsd, price, signedPercent, tokenName, utcTime } from "../format";
import {
  fetchAsset,
  fetchDailyHistory,
  fetchHistory,
  fetchToken,
  knownRange,
  knownTotal,
  parseTokenId,
  shortAddress,
  tickerOf,
  type AssetDetail,
  type HistoryRange,
  type TokenDetail,
  type TokenHistory,
} from "../token-api";
import { FIGURE_COPY, priceNote, tokenKind, type TokenKind } from "../token-kind";
import { dailyFlows } from "../flows";
import { HistoryChart, type HistoryStatus } from "./HistoryChart";
import type { SeriesKey } from "./history-chart";
import styles from "./TokenPanel.module.css";

const REFRESH_MS = 60_000;
const PHONE = "(max-width: 640px)";

// ---------------------------------------------------------------- data

type Remote<T> = { kind: "idle" } | { kind: "loading" } | { kind: "error"; message: string } | { kind: "ready"; data: T };

const describe = (error: unknown) => (error instanceof Error ? error.message : String(error));

/**
 * Loads whatever `key` names and refreshes it every minute (the API is
 * edge-cached, so refreshes are cheap). A refresh that fails keeps the data
 * already shown; a new key shows as loading until its own answer lands.
 */
function useRemote<T>(key: string | null, fetcher: (key: string, signal: AbortSignal) => Promise<T>): Remote<T> {
  const [load, setLoad] = useState<{ for: string; data: T | null; error: string | null }>({ for: "", data: null, error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (key === null) return;
    const controller = new AbortController();
    fetcher(key, controller.signal)
      .then((data) => setLoad({ for: key, data, error: null }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setLoad((current) => (current.for === key && current.data ? current : { for: key, data: null, error: describe(error) }));
      });
    return () => controller.abort();
  }, [key, fetcher, tick]);

  useEffect(() => {
    if (key === null) return;
    const timer = setInterval(() => setTick((t) => t + 1), REFRESH_MS);
    return () => clearInterval(timer);
  }, [key]);

  if (key === null) return { kind: "idle" };
  if (load.for !== key) return { kind: "loading" };
  if (load.data) return { kind: "ready", data: load.data };
  return { kind: "error", message: load.error ?? "unknown error" };
}

const loadToken = (id: string, signal: AbortSignal) => fetchToken(id, signal);
const loadAsset = (ticker: string, signal: AbortSignal) => fetchAsset(ticker, signal);
/** The last 30 UTC days, for the net flow bars. */
const loadDailyFlows = (id: string, signal: AbortSignal) =>
  fetchDailyHistory(id, 30, signal).then((h) => dailyFlows(h.series.filter((p): p is typeof p & { date: string } => typeof p.date === "string")));
/** The class board a class token sits on, for its profile. */
const loadClassBoard = (id: string, signal: AbortSignal) => fetchClassBoard(id as BoardClassId, signal);

/** The board entry for a token: its primary token id, or any of its deployments. */
function findEntry(entries: readonly ClassEntry[], tokenId: string): ClassEntry | null {
  const id = tokenId.toLowerCase();
  return entries.find((e) => e.token_id.toLowerCase() === id || e.deployments.some((d) => `${d.chain}:${d.address}`.toLowerCase() === id)) ?? null;
}

/** History keys are `<id>|<range>` so one hook covers both inputs. */
const loadHistory = (key: string, signal: AbortSignal): Promise<TokenHistory> => {
  const at = key.lastIndexOf("|");
  return fetchHistory(key.slice(0, at), key.slice(at + 1) as HistoryRange, signal);
};

// ---------------------------------------------------------------- pieces

function CopyButton({ text, label }: { text: string; label: string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  useEffect(() => {
    if (state === "idle") return;
    const timer = setTimeout(() => setState("idle"), 1600);
    return () => clearTimeout(timer);
  }, [state]);
  return (
    <button
      aria-label={state === "idle" ? label : undefined}
      aria-live="polite"
      className={styles.copy}
      onClick={() => {
        if (!navigator.clipboard) {
          setState("failed");
          return;
        }
        navigator.clipboard.writeText(text).then(
          () => setState("copied"),
          () => setState("failed"),
        );
      }}
      type="button"
    >
      {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : "Copy"}
    </button>
  );
}

function Figure({ label, value, note, tone }: { label: string; value: string | null; note?: ReactNode; tone?: "up" | "down" }) {
  return (
    <li className={styles.figure}>
      <span className={styles.figureLabel}>{label}</span>
      <span className={value === null ? styles.figureAbsent : styles.figureValue} data-tone={value === null ? undefined : tone}>
        {value ?? "—"}
      </span>
      {note ? <span className={styles.figureNote}>{note}</span> : null}
    </li>
  );
}

function Section({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <section aria-label={title} className={styles.section}>
      <header className={styles.sectionHead}>
        <h3>{title}</h3>
        {note ? <span>{note}</span> : null}
      </header>
      {children}
    </section>
  );
}

const tone = (change: number | null): "up" | "down" | undefined => (change === null ? undefined : change > 0 ? "up" : change < 0 ? "down" : undefined);

function changeNote(change: number | null, what: string): ReactNode {
  if (change === null) return `${what} change builds after a day of history`;
  return (
    <span className={change > 0 ? styles.up : change < 0 ? styles.down : undefined}>
      {signedPercent(change)} vs 24h ago
    </span>
  );
}

/** One line per chain the token is live on: label, contract, explorer link, supply there. */
function chainRows(token: TokenDetail["token"]) {
  const deployments = token.deployments ?? [];
  return token.chains.map((chain) => {
    const deployment = deployments.find((d) => d.chain === chain);
    const address = deployment?.address ?? (chain === token.chain ? token.address : null);
    return {
      chain,
      label: chainName(chain),
      address,
      supply: deployment?.supply ?? null,
      url: address ? explorerUrl(chain, address) : null,
    };
  });
}

/** A class token (a fund or note from the registry) rather than a stock token on a venue: its "venue" is the issuer. */
const isClassToken = (token: Pick<TokenDetail["token"], "venue">): boolean => token.venue.id.startsWith("issuer:");

function Identity({ detail }: { detail: TokenDetail }) {
  const { token, latest } = detail;
  const chains = chainRows(token);
  const classToken = isClassToken(token);
  const basis =
    token.price_basis === "issuer"
      ? "issuer quote"
      : token.price_basis === "market"
        ? "market trade"
        : classToken
          ? priceBasisLabel(token.price_basis)
          : (token.price_basis ?? null);
  // "Chainlink · Chainlink": the provider adds nothing when it names the basis.
  const provider = token.price_provider && token.price_provider.toLowerCase() !== (basis ?? "").toLowerCase() ? token.price_provider : null;
  return (
    <dl className={styles.meta}>
      <dt>{classToken ? "Issuer" : "Venue"}</dt>
      <dd>
        {token.venue.name}
        {token.status !== "active" ? <span className={styles.muted}> · {token.status}</span> : null}
      </dd>
      <dt>{chains.length === 1 ? "Chain" : "Chains"}</dt>
      <dd>
        <ul className={styles.chainList}>
          {chains.map((row) => (
            <li key={row.chain}>
              <span className={styles.chainLabel}>{row.label}</span>
              {row.address && row.address !== token.address ? (
                <span className={styles.muted} title={row.address}>
                  {shortAddress(row.address)}
                </span>
              ) : null}
              {row.url ? (
                <a className={styles.link} href={row.url} rel="noreferrer" target="_blank" title={`View the ${row.label} contract on the explorer`}>
                  explorer ↗
                </a>
              ) : null}
              {row.supply !== null && chains.length > 1 ? <span className={styles.muted}>supply {amount(row.supply)}</span> : null}
            </li>
          ))}
        </ul>
      </dd>
      <dt>Contract</dt>
      <dd>
        <span className={styles.inline}>
          <code className={styles.address}>{token.address}</code>
          <CopyButton label={`Copy the ${chainName(token.chain)} contract address`} text={token.address} />
        </span>
      </dd>
      {classToken && !token.ticker ? null : (
        <>
      <dt>Underlying</dt>
      <dd>
        {token.ticker ?? <span className={styles.muted}>not linked to a ticker</span>}
        {token.isin ? <span className={styles.muted}> · ISIN {token.isin}</span> : null}
        {latest?.underlying_price !== null && latest?.underlying_price !== undefined ? (
          <span className={styles.muted}>
            {" "}
            · stock {price(latest.underlying_price)}
            {latest.multiplier !== null ? ` · token/stock ${latest.multiplier.toFixed(4)}` : ""}
          </span>
        ) : null}
      </dd>
        </>
      )}
      <dt>Price basis</dt>
      <dd title={classToken ? priceBasisMeaning(token.price_basis) : undefined}>
        {basis ?? <span className={styles.muted}>not reported</span>}
        {provider ? <span className={styles.muted}> · {provider}</span> : null}
        {latest?.bid !== null && latest?.bid !== undefined && latest.ask !== null ? (
          <span className={styles.muted}>
            {" "}
            · bid {price(latest.bid)} · ask {price(latest.ask)}
          </span>
        ) : null}
      </dd>
      <dt>As of</dt>
      <dd>
        {detail.as_of ? utcTime(detail.as_of) : <span className={styles.muted}>no observation yet</span>}
        {latest?.price_at ? <span className={styles.muted}> · price at {utcTime(latest.price_at)}</span> : null}
      </dd>
      {token.first_seen ? (
        <>
          <dt>Tracked since</dt>
          <dd>{utcTime(token.first_seen)}</dd>
        </>
      ) : null}
    </dl>
  );
}

function Figures({ detail, kind }: { detail: TokenDetail; kind: TokenKind }) {
  const { token, latest } = detail;
  const robinhood = token.venue.id === "robinhood";
  const copy = FIGURE_COPY[kind];
  if (!latest) {
    return <p className={styles.status}>No observation yet: the first collection has not covered this token.</p>;
  }
  const change = latest.change_24h;
  return (
    <ul className={styles.figures}>
      <Figure
        label="Price"
        note={latest.halted ? <span className={styles.down}>trading halted</span> : priceNote(kind, token.price_basis, latest.price_at ? `priced ${ago(latest.price_at)}` : null)}
        value={latest.price === null ? null : price(latest.price)}
      />
      <Figure
        label="24h change"
        note={change.price === null ? "builds after a day of history" : copy.change}
        tone={tone(change.price)}
        value={change.price === null ? null : signedPercent(change.price)}
      />
      <Figure label="Market cap" note={changeNote(change.market_cap, "cap")} value={latest.market_cap === null ? null : compactUsd(latest.market_cap)} />
      <Figure
        label="Supply"
        note={token.chains.length > 1 ? `tokens across ${token.chains.length} chains` : changeNote(change.supply, "supply")}
        value={latest.supply === null ? null : amount(latest.supply)}
      />
      <Figure
        label={copy.volumeLabel}
        note={latest.volume_usd === null ? copy.volumeMissing : copy.volumePresent}
        value={latest.volume_usd === null ? null : compactUsd(latest.volume_usd)}
      />
      <Figure
        label={copy.mintBurnLabel}
        note={latest.mint_burn_usd === null ? copy.mintBurnMissing : `${copy.mintBurnPresent}, ${robinhood ? "today" : "last 24h"}`}
        value={latest.mint_burn_usd === null ? null : compactUsd(latest.mint_burn_usd)}
      />
    </ul>
  );
}

/** A figure this venue or index does not report: a dash, with the reason on hover. */
const absent = (
  <span className={styles.absent} title="Not reported">
    —
  </span>
);

function Total({ figure, format, reason }: { figure: { total: number | null; known: number; of: number }; format: (value: number) => string; reason: string }) {
  if (figure.total === null) return <span className={styles.reason}>{reason}</span>;
  return (
    <>
      {format(figure.total)}
      {figure.known < figure.of ? (
        <span className={styles.coverage} title={`${figure.known} of ${figure.of} tokens report this`}>
          {figure.known}/{figure.of}
        </span>
      ) : null}
    </>
  );
}

function AcrossVenues({ asset, currentId, onOpen }: { asset: AssetDetail; currentId: string; onOpen: (id: string) => void }) {
  const fresh = asset.tokens.filter((t) => !t.stale);
  const spread = knownRange(fresh.map((t) => t.price));
  const supply = knownTotal(fresh.map((t) => t.supply));
  const cell = (value: number | null, format: (v: number) => string, reason: string) => (
    <td className={styles.num}>{value === null ? <span className={styles.reason}>{reason}</span> : format(value)}</td>
  );
  return (
    <>
      <div aria-label="Across venues" className={styles.scroll} role="region" tabIndex={0}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Venue</th>
              <th className={styles.num} scope="col">
                Price
              </th>
              <th className={styles.num} scope="col">
                Market cap
              </th>
              <th className={styles.num} scope="col">
                Supply
              </th>
            </tr>
          </thead>
          <tbody>
            {asset.tokens.map((token) => {
              const current = token.id === currentId;
              return (
                <tr aria-current={current ? "true" : undefined} data-stale={token.stale || undefined} key={token.id}>
                  <td>
                    {current ? (
                      <span className={styles.chainLabel}>{token.venue.name}</span>
                    ) : (
                      <button className={styles.venueButton} onClick={() => onOpen(token.id)} title={`Open ${token.symbol} on ${token.venue.name}`} type="button">
                        {token.venue.name}
                      </button>
                    )}
                    {token.stale ? (
                      <span className={styles.stale} title="Latest observation is more than 24h older than the newest; left out of the totals">
                        stale
                      </span>
                    ) : null}
                    <span className={styles.sub}>
                      {token.symbol} · {token.chains.map(chainName).join(", ")}
                    </span>
                  </td>
                  {cell(token.price, price, "no price")}
                  {cell(token.market_cap, compactUsd, token.price === null ? "no price" : "no supply read")}
                  {cell(token.supply, amount, "not read")}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>
                Combined
                <span className={styles.sub}>
                  {asset.totals.venues} {asset.totals.venues === 1 ? "venue" : "venues"} · {asset.totals.tokens} {asset.totals.tokens === 1 ? "token" : "tokens"}
                  {asset.totals.stale_tokens ? ` · ${asset.totals.stale_tokens} stale` : ""}
                </span>
              </td>
              <td className={styles.num}>
                {spread === null ? <span className={styles.reason}>no prices</span> : spread.min === spread.max ? price(spread.min) : `${price(spread.min)} – ${price(spread.max)}`}
                {spread !== null && spread.min !== spread.max ? <span className={styles.sub}>spread</span> : null}
              </td>
              <td className={styles.num}>{asset.totals.market_cap === null ? <span className={styles.reason}>no caps</span> : compactUsd(asset.totals.market_cap)}</td>
              <td className={styles.num}>
                <Total figure={supply} format={amount} reason="none read" />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className={styles.note}>{asset.notes}</p>
    </>
  );
}

// ---------------------------------------------------------------- the panel

export function TokenPanel({
  tokenId,
  onClose,
  onOpen,
  classId = null,
}: {
  tokenId: string | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  /** The class page the panel opened from, which tells a commodity or stablecoin token from a fund. */
  classId?: string | null;
}) {
  const open = tokenId !== null;
  const panelRef = useRef<HTMLElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const [range, setRange] = useState<HistoryRange>("24h");
  const [series, setSeries] = useState<SeriesKey>("price");

  const detail = useRemote(tokenId, loadToken);
  const history = useRemote(tokenId === null ? null : `${tokenId}|${range}`, loadHistory);
  const flows = useRemote(tokenId, loadDailyFlows);
  const token = detail.kind === "ready" ? detail.data.token : null;
  const ticker = token ? (token.ticker ?? tickerOf(token.asset_id)) : null;
  const asset = useRemote(ticker, loadAsset);
  // A class token's board entry carries its cited profile (DQ-15); the board is the one the page shows.
  const boardId = classId && isBoardClass(classId as AssetClassId) ? classById(classId as AssetClassId).apiId : null;
  const classBoard = useRemote(boardId, loadClassBoard);
  const entry = classBoard.kind === "ready" && tokenId ? findEntry(classBoard.data.entries, tokenId) : null;
  const about = profileRows(entry?.profile);

  // Focus moves into the panel when it opens (or shows another token) and
  // back to whatever opened it when it closes.
  useEffect(() => {
    if (tokenId === null) return;
    const active = document.activeElement;
    const panel = panelRef.current;
    if (active instanceof HTMLElement && panel && !panel.contains(active)) opener.current = active;
    panel?.focus();
    panel?.scrollTo({ top: 0 });
  }, [tokenId]);

  useEffect(() => {
    if (!open) return;
    return () => {
      opener.current?.focus();
      opener.current = null;
    };
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Phones: the panel covers the page, so the page must not scroll underneath it.
  useEffect(() => {
    if (!open) return;
    const query = matchMedia(PHONE);
    const apply = () => {
      document.body.style.overflow = query.matches ? "hidden" : "";
    };
    apply();
    query.addEventListener("change", apply);
    return () => {
      query.removeEventListener("change", apply);
      document.body.style.overflow = "";
    };
  }, [open]);

  if (tokenId === null) return null;

  const parsed = parseTokenId(tokenId);
  const chainLabel = token ? chainName(token.chain) : parsed ? chainName(parsed.chain) : null;
  const historyStatus: HistoryStatus =
    history.kind === "ready" ? { kind: "ready", history: history.data } : history.kind === "error" ? { kind: "error", message: history.message } : { kind: "loading" };

  return (
    <aside aria-labelledby={titleId} className={styles.panel} ref={panelRef} role="dialog" tabIndex={-1}>
      <header className={styles.head}>
        <div className={styles.titles}>
          <p className={styles.eyebrow}>
            Token{token ? ` · ${token.venue.name}` : ""}
            {chainLabel && chainLabel !== token?.venue.name ? ` · ${chainLabel}` : ""}
          </p>
          <h2 className={styles.title} id={titleId}>
            {token ? (
              <>
                {token.symbol}
                {token.name ? (
                  <>
                    {" "}
                    <span className={styles.name}>{tokenName(token.name)}</span>
                  </>
                ) : null}
                {detail.kind === "ready" && detail.data.latest?.halted ? <span className={styles.halted}>halted</span> : null}
              </>
            ) : detail.kind === "error" ? (
              "Token not found"
            ) : (
              <>
                Loading token
                {parsed ? <span className={styles.name}>{shortAddress(parsed.address)}</span> : null}
              </>
            )}
          </h2>
        </div>
        <button className={styles.close} onClick={onClose} type="button">
          Close <kbd aria-hidden>esc</kbd>
        </button>
      </header>

      <div className={styles.body}>
        {detail.kind === "error" ? (
          <p className={styles.status}>
            <span className={styles.error}>{detail.message}</span>
            {parsed ? (
              <>
                <br />
                Requested <code>{tokenId}</code>. Pick a token from the grid, or check the link.
              </>
            ) : (
              <>
                <br />
                <code>{tokenId}</code> is not a token id. Expected <code>chain:address</code>.
              </>
            )}
          </p>
        ) : detail.kind === "ready" ? (
          <>
            <Identity detail={detail.data} />
            <Figures detail={detail.data} kind={tokenKind({ venueId: detail.data.token.venue.id, tokenId: detail.data.token.id, classId })} />
            {about.length ? (
              <Section note="from the issuer's own documents; each fact links to its source" title={classId === "commodity" ? "About this product" : "About this fund"}>
                <dl className={styles.about}>
                  {about.map((row) => (
                    <div key={row.key}>
                      <dt>{row.label}</dt>
                      <dd>
                        {row.value}
                        <a className={styles.aboutSource} href={row.source_url} rel="noreferrer" target="_blank" title={row.source_url}>
                          {sourceLabel(row.source_url)} ↗
                        </a>
                      </dd>
                    </div>
                  ))}
                </dl>
              </Section>
            ) : null}
            <Section note={history.kind === "ready" ? `${history.data.observations} observations, 30 min apart · UTC` : undefined} title="History">
              <HistoryChart
                flows={flows.kind === "ready" && flows.data.some(([, v]) => v !== null) ? flows.data : undefined}
                onRange={setRange}
                onSeries={setSeries}
                range={range}
                series={series}
                status={historyStatus}
              />
            </Section>
            {ticker === null && isClassToken(detail.data.token) ? null : (
            <Section
              note={
                asset.kind === "ready"
                  ? `${asset.data.asset.name ?? asset.data.asset.ticker} · combined ${asset.data.totals.market_cap === null ? "—" : compactUsd(asset.data.totals.market_cap)}`
                  : ticker
                    ? "every tokenized version of this stock"
                    : undefined
              }
              title={ticker ? `Across venues · ${ticker}` : "Across venues"}
            >
              {ticker === null ? (
                <p className={styles.status}>This token is not linked to a listed ticker yet, so there is no cross-venue view.</p>
              ) : asset.kind === "ready" ? (
                <AcrossVenues asset={asset.data} currentId={detail.data.token.id} onOpen={onOpen} />
              ) : asset.kind === "error" ? (
                <p className={styles.status}>
                  <span className={styles.error}>Cross-venue view unavailable: {asset.message}</span>
                </p>
              ) : (
                <p className={styles.status}>Reading other venues…</p>
              )}
            </Section>
            )}
          </>
        ) : (
          <p className={styles.status}>Reading token…</p>
        )}
      </div>
    </aside>
  );
}
